/**
 * User Manager - Centralized user state and admin management
 * 
 * Single source of truth for all user-related state and operations
 * Handles authentication, admin status, and permissions with proper caching
 */

import { User, Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { logger } from '../utils/logger'

export interface AdminPermissions {
  can_view_all_data?: boolean
  can_delete_all_slots?: boolean
  can_export_data?: boolean
  can_manage_admins?: boolean
}

export interface UserState {
  user: User | null
  session: Session | null
  loading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  adminPermissions: AdminPermissions
}

type UserStateListener = (state: UserState) => void

class UserManager {
  private state: UserState = {
    user: null,
    session: null,
    loading: true,
    isAuthenticated: false,
    isAdmin: false,
    adminPermissions: {}
  }

  private listeners: Set<UserStateListener> = new Set()
  private adminCheckCache: Map<string, { isAdmin: boolean; permissions: AdminPermissions; timestamp: number }> = new Map()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  private isInitialized = false

  constructor() {
    this.initialize()
  }

  /**
   * Initialize the user manager with Supabase auth
   */
  private async initialize() {
    logger.info('Initializing UserManager', { component: 'UserManager' })

    try {
      // Get initial session
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) {
        logger.error('Failed to get initial session', {
          component: 'UserManager',
          data: error
        })
      }

      await this.updateUserState(session)

      // Listen for auth changes
      supabase.auth.onAuthStateChange(async (event, session) => {
        logger.authEvent(`USER_MANAGER_${event}`, session?.user)
        await this.updateUserState(session)
      })

      this.isInitialized = true
      logger.info('UserManager initialized successfully', { component: 'UserManager' })
    } catch (error) {
      logger.error('UserManager initialization failed', {
        component: 'UserManager',
        data: error
      })
    }
  }

  /**
   * Update user state and check admin status
   */
  private async updateUserState(session: Session | null) {
    const newUser = session?.user || null
    
    this.state = {
      ...this.state,
      user: newUser,
      session,
      isAuthenticated: !!newUser,
      loading: false
    }

    // Check admin status if user exists
    if (newUser?.email) {
      await this.checkAndUpdateAdminStatus(newUser.email)
    } else {
      this.state.isAdmin = false
      this.state.adminPermissions = {}
    }

    this.notifyListeners()
  }

  /**
   * Check admin status with caching
   */
  private async checkAndUpdateAdminStatus(email: string) {
    try {
      // Check cache first
      const cached = this.adminCheckCache.get(email)
      const now = Date.now()

      if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
        logger.debug('Using cached admin status', {
          component: 'UserManager',
          data: { email, isAdmin: cached.isAdmin }
        })
        
        this.state.isAdmin = cached.isAdmin
        this.state.adminPermissions = cached.permissions
        return
      }

      // Check admin status from database
      logger.debug('Checking admin status from database', {
        component: 'UserManager',
        data: { email }
      })

      const { data: adminCheck, error: adminError } = await supabase
        .rpc('is_user_admin', { user_email: email })

      if (adminError) {
        logger.error('Error checking admin status', {
          component: 'UserManager',
          data: { email, error: adminError }
        })
        this.state.isAdmin = false
        this.state.adminPermissions = {}
        return
      }

      this.state.isAdmin = adminCheck || false

      // Get permissions if admin
      let permissions: AdminPermissions = {}
      if (adminCheck) {
        const { data: permData, error: permError } = await supabase
          .rpc('get_user_admin_permissions', { user_email: email })

        if (permError) {
          logger.error('Error getting admin permissions', {
            component: 'UserManager',
            data: { email, error: permError }
          })
        } else {
          permissions = permData || {}
        }
      }

      this.state.adminPermissions = permissions

      // Cache the result
      this.adminCheckCache.set(email, {
        isAdmin: this.state.isAdmin,
        permissions,
        timestamp: now
      })

      logger.info('Admin status updated', {
        component: 'UserManager',
        data: { 
          email, 
          isAdmin: this.state.isAdmin,
          hasPermissions: Object.keys(permissions).length > 0
        }
      })

    } catch (error) {
      logger.error('Exception in admin status check', {
        component: 'UserManager',
        data: { email, error }
      })
      
      this.state.isAdmin = false
      this.state.adminPermissions = {}
    }
  }

  /**
   * Get current user state
   */
  getState(): UserState {
    return { ...this.state }
  }

  /**
   * Get specific user properties
   */
  getUser(): User | null {
    return this.state.user
  }

  getSession(): Session | null {
    return this.state.session
  }

  isAuthenticated(): boolean {
    return this.state.isAuthenticated
  }

  isAdmin(): boolean {
    return this.state.isAdmin
  }

  getAdminPermissions(): AdminPermissions {
    return { ...this.state.adminPermissions }
  }

  isLoading(): boolean {
    return this.state.loading
  }

  /**
   * Force refresh admin status (bypass cache)
   */
  async refreshAdminStatus(): Promise<void> {
    if (!this.state.user?.email) {
      return
    }

    // Clear cache for current user
    this.adminCheckCache.delete(this.state.user.email)
    
    // Recheck admin status
    await this.checkAndUpdateAdminStatus(this.state.user.email)
    this.notifyListeners()
  }

  /**
   * Sign out user
   */
  async signOut(): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info('User signing out', {
        component: 'UserManager',
        data: { email: this.state.user?.email }
      })

      const { error } = await supabase.auth.signOut()
      
      if (error) {
        logger.error('Sign out failed', {
          component: 'UserManager',
          data: error
        })
        return { success: false, error: error.message }
      }

      // Clear cache
      this.adminCheckCache.clear()
      
      return { success: true }
    } catch (error) {
      logger.error('Sign out exception', {
        component: 'UserManager',
        data: error
      })
      return { success: false, error: 'Sign out failed' }
    }
  }

  /**
   * Subscribe to user state changes
   */
  subscribe(listener: UserStateListener): () => void {
    this.listeners.add(listener)
    
    // Immediately call listener with current state
    listener(this.getState())

    // Return unsubscribe function
    return () => {
      this.listeners.delete(listener)
    }
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners() {
    const currentState = this.getState()
    this.listeners.forEach(listener => {
      try {
        listener(currentState)
      } catch (error) {
        logger.error('UserManager listener error', {
          component: 'UserManager',
          data: error
        })
      }
    })
  }

  /**
   * Check if specific admin permission exists
   */
  hasPermission(permission: keyof AdminPermissions): boolean {
    return this.state.isAdmin && !!this.state.adminPermissions[permission]
  }

  /**
   * Get debug information
   */
  getDebugInfo() {
    return {
      isInitialized: this.isInitialized,
      userEmail: this.state.user?.email,
      isAdmin: this.state.isAdmin,
      permissions: this.state.adminPermissions,
      cacheSize: this.adminCheckCache.size,
      listenersCount: this.listeners.size
    }
  }

  /**
   * Clear admin cache (useful for debugging)
   */
  clearAdminCache() {
    this.adminCheckCache.clear()
    logger.info('Admin cache cleared', { component: 'UserManager' })
  }
}

// Export singleton instance
export const userManager = new UserManager()
export default userManager